import React, { useEffect, useState, useCallback } from "react";
import {
  Typography, Box, Paper, FormControl, InputLabel, Select, MenuItem, Button,
  Dialog, DialogTitle, DialogContent, IconButton, Snackbar,
  DialogActions, List, ListItem, ListItemText,
  Checkbox, ListItemIcon, ListItemButton, CircularProgress, TextField
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  fetchCustomers,
  fetchPlayersByCustomer,
  deletePlayer,
  fetchAllContent,
  updateAllowedContentForPlayer,
  fetchAllowedContent,
  renameCustomer,
  createCustomer,
  deleteCustomer,
  createPlayer,
  updatePlayer,
  calculateMissingHashes,
  clearPlayerContent,
  Content,
  fetchAdSlotPolicies,
  createAdSlotPolicy,
  updateAdSlotPolicy,
  deleteAdSlotPolicy,
  AdSlotPolicy,
  fetchDayParts,
  createDayPart,
  updateDayPart,
  deleteDayPart,
  CustomerDayPart,
} from "../api/api";
import Feeds from "./Feeds";
import UserManagement from "../components/Admin/UserManagement";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";

type Player = {
  sn: string;
  location: string;
  time_zone?: string;
};

// Hilfsfunktion: Berechnet UTC-Offset für eine Zeitzone
const getUTCOffset = (timeZone: string): string => {
  try {
    if (timeZone === 'UTC') {
      return '(UTC+00:00)';
    }
    
    const now = new Date();
    
    // Erstelle zwei Formatter: einen für UTC und einen für die Ziel-Zeitzone
    // Verwende ein festes Datum, um konsistente Ergebnisse zu erhalten
    const utcFormatter = new Intl.DateTimeFormat('en', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const tzFormatter = new Intl.DateTimeFormat('en', {
      timeZone: timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    // Formatiere die aktuelle Zeit in beiden Zeitzonen
    const utcParts = utcFormatter.formatToParts(now);
    const tzParts = tzFormatter.formatToParts(now);
    
    const utcHours = parseInt(utcParts.find(p => p.type === 'hour')?.value || '0', 10);
    const utcMinutes = parseInt(utcParts.find(p => p.type === 'minute')?.value || '0', 10);
    const tzHours = parseInt(tzParts.find(p => p.type === 'hour')?.value || '0', 10);
    const tzMinutes = parseInt(tzParts.find(p => p.type === 'minute')?.value || '0', 10);
    
    // Berechne die Differenz in Minuten
    const utcTotalMinutes = utcHours * 60 + utcMinutes;
    const tzTotalMinutes = tzHours * 60 + tzMinutes;
    let diffMinutes = tzTotalMinutes - utcTotalMinutes;
    
    // Berücksichtige mögliche Tageswechsel (Offset kann zwischen -12 und +14 Stunden liegen)
    if (diffMinutes > 12 * 60) {
      diffMinutes -= 24 * 60;
    } else if (diffMinutes < -12 * 60) {
      diffMinutes += 24 * 60;
    }
    
    const sign = diffMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(diffMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    
    return `(UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`;
  } catch (error) {
    console.error(`Error calculating UTC offset for ${timeZone}:`, error);
    return '(UTC)';
  }
};

const AdminPanel: React.FC = () => {
  const { t } = useTranslation();

  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [showFeedConfig, setShowFeedConfig] = useState<string | null>(null);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);

  // States for player deletion confirmation dialog
  const [confirmDeletePlayerDialogOpen, setConfirmDeletePlayerDialogOpen] = useState(false);
  const [playerToDeleteSN, setPlayerToDeleteSN] = useState<string | null>(null);

  // NEW: States for content assignment dialog
  const [contentAssignmentDialogOpen, setContentAssignmentDialogOpen] = useState(false);

  // States for clear content dialog
  const [confirmClearContentDialogOpen, setConfirmClearContentDialogOpen] = useState(false);
  const [playerToClearContentSN, setPlayerToClearContentSN] = useState<string | null>(null);
  const [selectedPlayerForContentAssignment, setSelectedPlayerForContentAssignment] = useState<string | null>(null);
  const [allAvailableContent, setAllAvailableContent] = useState<Content[]>([]);
  const [assignedContentNames, setAssignedContentNames] = useState<string[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);

  // States for rename customer dialog
  const [renameCustomerDialogOpen, setRenameCustomerDialogOpen] = useState(false);
  const [customerToRename, setCustomerToRename] = useState<{ id: string; name: string } | null>(null);
  const [newCustomerName, setNewCustomerName] = useState("");

  // States for create customer dialog
  const [createCustomerDialogOpen, setCreateCustomerDialogOpen] = useState(false);
  const [newCustomerNameInput, setNewCustomerNameInput] = useState("");

  // States for delete customer confirmation dialog
  const [confirmDeleteCustomerDialogOpen, setConfirmDeleteCustomerDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<{ id: string; name: string } | null>(null);

  // States for create player dialog
  const [createPlayerDialogOpen, setCreatePlayerDialogOpen] = useState(false);
  const [newPlayerSN, setNewPlayerSN] = useState("");
  const [newPlayerLocation, setNewPlayerLocation] = useState("");
  const [newPlayerTimeZone, setNewPlayerTimeZone] = useState("");

  // States for edit player dialog
  const [editPlayerDialogOpen, setEditPlayerDialogOpen] = useState(false);
  const [playerToEdit, setPlayerToEdit] = useState<Player | null>(null);
  const [editPlayerLocation, setEditPlayerLocation] = useState("");
  const [editPlayerTimeZone, setEditPlayerTimeZone] = useState("");

  // Tageszeiten (Day parts)
  const [dayParts, setDayParts] = useState<CustomerDayPart[]>([]);
  const [createDayPartDialogOpen, setCreateDayPartDialogOpen] = useState(false);
  const [newDayPartName, setNewDayPartName] = useState("");
  const [newDayPartFromTime, setNewDayPartFromTime] = useState("");
  const [editDayPartDialogOpen, setEditDayPartDialogOpen] = useState(false);
  const [dayPartToEdit, setDayPartToEdit] = useState<CustomerDayPart | null>(null);
  const [editDayPartName, setEditDayPartName] = useState("");
  const [editDayPartFromTime, setEditDayPartFromTime] = useState("");
  const [dayPartToDelete, setDayPartToDelete] = useState<CustomerDayPart | null>(null);
  const [confirmDeleteDayPartDialogOpen, setConfirmDeleteDayPartDialogOpen] = useState(false);

  // Ad-Slot-Policies (Werbe-Slot Richtlinie)
  const [adSlotPolicies, setAdSlotPolicies] = useState<AdSlotPolicy[]>([]);
  const [createPolicyDialogOpen, setCreatePolicyDialogOpen] = useState(false);
  const [editPolicyDialogOpen, setEditPolicyDialogOpen] = useState(false);
  const [policyToEdit, setPolicyToEdit] = useState<AdSlotPolicy | null>(null);
  const [policyToDelete, setPolicyToDelete] = useState<AdSlotPolicy | null>(null);
  const [confirmDeletePolicyDialogOpen, setConfirmDeletePolicyDialogOpen] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState("");
  const [newPolicyMaxAdTimePerHour, setNewPolicyMaxAdTimePerHour] = useState("");
  const [newPolicyMaxAdsPerBreak, setNewPolicyMaxAdsPerBreak] = useState("");
  const [newPolicyMaxBreakDuration, setNewPolicyMaxBreakDuration] = useState("");
  const [newPolicyMinGapBetweenBreaks, setNewPolicyMinGapBetweenBreaks] = useState("");
  const [newPolicyPreferredInterval, setNewPolicyPreferredInterval] = useState("");
  const [newPolicyTargetAdRatio, setNewPolicyTargetAdRatio] = useState("");
  const [newPolicyRespectContentBreakpoints] = useState(true); // kept for HMR/compat, field removed from DB
  const [newPolicyMidrollMode, setNewPolicyMidrollMode] = useState<"none" | "breakpoint" | "anytime">("none");
  const [newPolicyPostrollMode, setNewPolicyPostrollMode] = useState<"none" | "active">("none");
  const [editPolicyName, setEditPolicyName] = useState("");
  const [editPolicyMaxAdTimePerHour, setEditPolicyMaxAdTimePerHour] = useState("");
  const [editPolicyMaxAdsPerBreak, setEditPolicyMaxAdsPerBreak] = useState("");
  const [editPolicyMaxBreakDuration, setEditPolicyMaxBreakDuration] = useState("");
  const [editPolicyMinGapBetweenBreaks, setEditPolicyMinGapBetweenBreaks] = useState("");
  const [editPolicyPreferredInterval, setEditPolicyPreferredInterval] = useState("");
  const [editPolicyTargetAdRatio, setEditPolicyTargetAdRatio] = useState("");
  const [editPolicyRespectContentBreakpoints] = useState(true); // kept for HMR/compat, field removed from DB
  const [editPolicyMidrollMode, setEditPolicyMidrollMode] = useState<"none" | "breakpoint" | "anytime">("none");
  const [editPolicyPostrollMode, setEditPolicyPostrollMode] = useState<"none" | "active">("none");
  const [editPolicyDayPartId, setEditPolicyDayPartId] = useState<number | "">("");
  const [editPolicyIsDefault, setEditPolicyIsDefault] = useState(false);
  const [newPolicyDayPartId, setNewPolicyDayPartId] = useState<number | "">("");
  const [newPolicyIsDefault, setNewPolicyIsDefault] = useState(false);

  // Function to reload customer list (for CustomerManagement)
  const loadCustomers = useCallback(() => {
    fetchCustomers().then(setCustomers);
  }, []);

  // Function to reload players for the currently selected customer
  const loadPlayersForSelectedCustomer = useCallback(() => {
    if (selectedCustomer) {
      fetchPlayersByCustomer(selectedCustomer)
        .then(setPlayers)
        .catch(error => {
          console.error("Error loading players for customer:", error);
          setSnackbarMsg(t("playerManagement.errorLoadingPlayers"));
        });
    } else {
      setPlayers([]);
    }
  }, [selectedCustomer, t]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    // When the selected customer changes, reload players for this customer
    loadPlayersForSelectedCustomer();
  }, [selectedCustomer, loadPlayersForSelectedCustomer]);

  const loadAdSlotPolicies = useCallback(() => {
    if (selectedCustomer) {
      fetchAdSlotPolicies(selectedCustomer).then(setAdSlotPolicies).catch(() => setAdSlotPolicies([]));
    } else {
      setAdSlotPolicies([]);
    }
  }, [selectedCustomer]);

  const loadDayParts = useCallback(() => {
    if (selectedCustomer) {
      fetchDayParts(selectedCustomer).then(setDayParts).catch(() => setDayParts([]));
    } else {
      setDayParts([]);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    loadAdSlotPolicies();
  }, [loadAdSlotPolicies]);

  useEffect(() => {
    loadDayParts();
  }, [loadDayParts]);

  // Handler for deleting a player
  const handleDeletePlayerClick = (sn: string) => {
    setPlayerToDeleteSN(sn);
    setConfirmDeletePlayerDialogOpen(true);
  };

  const handleConfirmDeletePlayer = async () => {
    if (playerToDeleteSN && selectedCustomer) {
      try {
        await deletePlayer(selectedCustomer, playerToDeleteSN);
        setSnackbarMsg(t("playerManagement.playerDeleted"));
        loadPlayersForSelectedCustomer(); // Reload player list
      } catch (error: any) {
        if (error && error.data && error.data.error === "player_assigned_to_users") {
          setSnackbarMsg(t("playerManagement.playerAssignedError"));
        } else {
          setSnackbarMsg(t("common.deletePlayerError"));
          console.error("Error deleting player:", error);
        }
      }
    }
    setConfirmDeletePlayerDialogOpen(false);
    setPlayerToDeleteSN(null);
  };

  const handleCancelDeletePlayer = () => {
    setConfirmDeletePlayerDialogOpen(false);
    setPlayerToDeleteSN(null);
  };

  // Handler for clearing player content
  const handleClearContentClick = (sn: string) => {
    setPlayerToClearContentSN(sn);
    setConfirmClearContentDialogOpen(true);
  };

  const handleConfirmClearContent = async () => {
    if (playerToClearContentSN) {
      try {
        await clearPlayerContent(playerToClearContentSN);
        setSnackbarMsg(t("playerManagement.contentCleared"));
      } catch (error: any) {
        setSnackbarMsg(t("playerManagement.errorClearingContent"));
        console.error("Error clearing player content:", error);
      }
    }
    setConfirmClearContentDialogOpen(false);
    setPlayerToClearContentSN(null);
  };

  const handleCancelClearContent = () => {
    setConfirmClearContentDialogOpen(false);
    setPlayerToClearContentSN(null);
  };

  // NEW: Handler to open content assignment dialog
  const handleOpenContentAssignmentDialog = async (playerSN: string) => {
    setSelectedPlayerForContentAssignment(playerSN);
    setLoadingContent(true);
    try {
      // Fetch all available content
      const allContent = await fetchAllContent();
      
      // Prüfe welche Inhalte keinen SHA1-Hash oder keinen sha1_encrypted haben (für verschlüsselte Feeds)
      const contentNeedingHash = allContent.filter(c => !c.sha1 || !c.sha1_encrypted);
      
      if (contentNeedingHash.length > 0) {
        console.log(`[Content Assignment] ${contentNeedingHash.length} Inhalte mit fehlendem Hash (sha1 oder sha1_encrypted). Berechne fehlende Hashes...`);
        
        try {
          // Berechne fehlende Hashes (inkl. sha1_encrypted wo nötig)
          const fileNames = contentNeedingHash.map(c => c.file_name);
          const hashResults = await calculateMissingHashes(fileNames);
          
          // Aktualisiere die Content-Liste mit den berechneten Hashes
          const updatedContent = allContent.map(content => {
            const hashResult = hashResults.results.find(r => r.file_name === content.file_name);
            if (hashResult) {
              return {
                ...content,
                sha1: hashResult.sha1,
                ...(hashResult.sha1_encrypted != null && { sha1_encrypted: hashResult.sha1_encrypted }),
              };
            }
            return content;
          });
          
          setAllAvailableContent(updatedContent);
          
          if (hashResults.summary.calculated > 0) {
            console.log(`[Content Assignment] ${hashResults.summary.calculated} Hashes erfolgreich berechnet.`);
          }
          if (hashResults.summary.errors > 0) {
            console.warn(`[Content Assignment] ${hashResults.summary.errors} Fehler beim Berechnen von Hashes.`);
          }
        } catch (hashError) {
          console.error("Fehler beim Berechnen fehlender Hashes:", hashError);
          // Fehler beim Hash-Berechnen ist nicht kritisch, fahre mit vorhandenen Daten fort
          setAllAvailableContent(allContent);
        }
      } else {
        setAllAvailableContent(allContent);
      }

      // Fetch content currently assigned to this player
      const assigned = await fetchAllowedContent(playerSN);
      setAssignedContentNames(assigned.map((c: any) => c.content_name)); // Assuming fetchAllowedContent returns objects with content_name

      setContentAssignmentDialogOpen(true);
    } catch (error) {
      console.error("Error loading content for assignment:", error);
      setSnackbarMsg(t("contentManagement.errorLoadingContent"));
    } finally {
      setLoadingContent(false);
    }
  };

  // NEW: Handler for direct selection list content change
  const handleToggleContentAssignment = (contentName: string) => {
    setAssignedContentNames((prevSelected) => {
      if (prevSelected.includes(contentName)) {
        return prevSelected.filter((name) => name !== contentName);
      } else {
        return [...prevSelected, contentName];
      }
    });
  };

  // NEW: Handler to save content assignments
  const handleSaveContentAssignment = async () => {
    if (!selectedPlayerForContentAssignment) return;

    try {
      await updateAllowedContentForPlayer(selectedPlayerForContentAssignment, assignedContentNames);
      setSnackbarMsg(t("contentManagement.contentAssignedSuccess"));
      setContentAssignmentDialogOpen(false);
    } catch (error) {
      console.error("Error saving content assignments:", error);
      setSnackbarMsg(t("contentManagement.errorSavingContent"));
    }
  };

  // Handler for opening rename customer dialog
  const handleRenameCustomer = (customer: { id: string; name: string }, event: React.MouseEvent) => {
    event.stopPropagation();
    setCustomerToRename(customer);
    setNewCustomerName(customer.name);
    setRenameCustomerDialogOpen(true);
  };

  // Handler for saving renamed customer
  const handleSaveRenameCustomer = async () => {
    if (!customerToRename || !newCustomerName.trim()) return;

    try {
      await renameCustomer(customerToRename.id, newCustomerName.trim());
      setSnackbarMsg(t("customerManagement.customerRenamed"));
      loadCustomers();
      // Update selected customer name if it's the renamed one
      if (selectedCustomer === customerToRename.id) {
        setCustomers(prev => prev.map(c => 
          c.id === customerToRename.id ? { ...c, name: newCustomerName.trim() } : c
        ));
      }
      setRenameCustomerDialogOpen(false);
      setCustomerToRename(null);
      setNewCustomerName("");
    } catch (error) {
      console.error("Error renaming customer:", error);
      setSnackbarMsg(t("customerManagement.errorRenamingCustomer"));
    }
  };

  const handleCancelRenameCustomer = () => {
    setRenameCustomerDialogOpen(false);
    setCustomerToRename(null);
    setNewCustomerName("");
  };

  // Handler for creating a new customer
  const handleCreateCustomer = async () => {
    if (!newCustomerNameInput.trim()) return;

    try {
      await createCustomer(newCustomerNameInput.trim());
      setSnackbarMsg(t("customerManagement.customerCreated"));
      loadCustomers();
      setCreateCustomerDialogOpen(false);
      setNewCustomerNameInput("");
    } catch (error: any) {
      if (error && error.error === "duplicate_name") {
        setSnackbarMsg(t("customerManagement.duplicateNameError"));
      } else {
        setSnackbarMsg(t("common.createCustomerError"));
        console.error("Error creating customer:", error);
      }
    }
  };

  // Handler for deleting a customer
  const handleDeleteCustomerClick = (customer: { id: string; name: string }, event: React.MouseEvent) => {
    event.stopPropagation();
    setCustomerToDelete(customer);
    setConfirmDeleteCustomerDialogOpen(true);
  };

  const handleConfirmDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      await deleteCustomer(customerToDelete.id);
      setSnackbarMsg(t("customerManagement.customerDeleted"));
      loadCustomers();
      if (selectedCustomer === customerToDelete.id) {
        setSelectedCustomer(null);
      }
    } catch (error) {
      setSnackbarMsg(t("common.deleteCustomerError"));
      console.error("Error deleting customer:", error);
    }
    setConfirmDeleteCustomerDialogOpen(false);
    setCustomerToDelete(null);
  };

  const handleCancelDeleteCustomer = () => {
    setConfirmDeleteCustomerDialogOpen(false);
    setCustomerToDelete(null);
  };

  // Handler for creating a new player
  const handleCreatePlayer = async () => {
    if (!selectedCustomer || !newPlayerSN.trim() || !newPlayerLocation.trim() || !newPlayerTimeZone.trim()) return;

    try {
      await createPlayer(selectedCustomer, newPlayerSN.trim(), newPlayerLocation.trim(), newPlayerTimeZone.trim());
      setSnackbarMsg(t("playerManagement.playerCreated"));
      loadPlayersForSelectedCustomer();
      setCreatePlayerDialogOpen(false);
      setNewPlayerSN("");
      setNewPlayerLocation("");
      setNewPlayerTimeZone("");
    } catch (error) {
      setSnackbarMsg(t("playerManagement.errorCreatingPlayer"));
      console.error("Error creating player:", error);
    }
  };

  // Handler for opening edit player dialog
  const handleEditPlayerClick = (player: Player, event: React.MouseEvent) => {
    event.stopPropagation();
    setPlayerToEdit(player);
    setEditPlayerLocation(player.location);
    setEditPlayerTimeZone(player.time_zone || "");
    setEditPlayerDialogOpen(true);
  };

  // Handler for saving edited player
  const handleSaveEditPlayer = async () => {
    if (!selectedCustomer || !playerToEdit || !editPlayerLocation.trim() || !editPlayerTimeZone.trim()) return;

    try {
      await updatePlayer(selectedCustomer, playerToEdit.sn, editPlayerLocation.trim(), editPlayerTimeZone.trim());
      setSnackbarMsg(t("playerManagement.playerUpdated"));
      loadPlayersForSelectedCustomer();
      setEditPlayerDialogOpen(false);
      setPlayerToEdit(null);
      setEditPlayerLocation("");
      setEditPlayerTimeZone("");
    } catch (error) {
      setSnackbarMsg(t("playerManagement.errorUpdatingPlayer"));
      console.error("Error updating player:", error);
    }
  };

  const handleCancelEditPlayer = () => {
    setEditPlayerDialogOpen(false);
    setPlayerToEdit(null);
    setEditPlayerLocation("");
    setEditPlayerTimeZone("");
  };

  const handleCreateDayPart = async () => {
    if (!selectedCustomer || !newDayPartName.trim() || !newDayPartFromTime.trim()) return;
    try {
      await createDayPart(selectedCustomer, { name: newDayPartName.trim(), from_time: newDayPartFromTime.trim() });
      setSnackbarMsg(t("dayParts.dayPartCreated"));
      loadDayParts();
      setCreateDayPartDialogOpen(false);
      setNewDayPartName("");
      setNewDayPartFromTime("");
    } catch (e) {
      setSnackbarMsg(String(e));
    }
  };

  const handleOpenEditDayPart = (dp: CustomerDayPart) => {
    setDayPartToEdit(dp);
    setEditDayPartName(dp.name);
    setEditDayPartFromTime(String(dp.from_time).slice(0, 8));
    setEditDayPartDialogOpen(true);
  };

  const handleSaveEditDayPart = async () => {
    if (!selectedCustomer || !dayPartToEdit || !editDayPartName.trim() || !editDayPartFromTime.trim()) return;
    try {
      await updateDayPart(selectedCustomer, dayPartToEdit.id, { name: editDayPartName.trim(), from_time: editDayPartFromTime.trim() });
      setSnackbarMsg(t("dayParts.dayPartUpdated"));
      loadDayParts();
      setEditDayPartDialogOpen(false);
      setDayPartToEdit(null);
    } catch (e) {
      setSnackbarMsg(String(e));
    }
  };

  const handleDeleteDayPartClick = (dp: CustomerDayPart) => {
    setDayPartToDelete(dp);
    setConfirmDeleteDayPartDialogOpen(true);
  };

  const handleConfirmDeleteDayPart = async () => {
    if (!selectedCustomer || !dayPartToDelete) return;
    try {
      await deleteDayPart(selectedCustomer, dayPartToDelete.id);
      setSnackbarMsg(t("dayParts.dayPartDeleted"));
      loadDayParts();
      setConfirmDeleteDayPartDialogOpen(false);
      setDayPartToDelete(null);
    } catch (e) {
      setSnackbarMsg(String(e));
    }
  };

  const handleCreatePolicy = async () => {
    if (!selectedCustomer || !newPolicyName.trim()) return;
    try {
      await createAdSlotPolicy(selectedCustomer, {
        name: newPolicyName.trim(),
        max_ad_time_per_hour: newPolicyMaxAdTimePerHour === "" ? null : Number(newPolicyMaxAdTimePerHour),
        max_ads_per_break: newPolicyMaxAdsPerBreak === "" ? null : Number(newPolicyMaxAdsPerBreak),
        max_break_duration: newPolicyMaxBreakDuration === "" ? null : Number(newPolicyMaxBreakDuration),
        min_gap_between_breaks: newPolicyMinGapBetweenBreaks === "" ? null : Number(newPolicyMinGapBetweenBreaks),
        preferred_interval: newPolicyPreferredInterval === "" ? null : Number(newPolicyPreferredInterval),
        target_ad_ratio: newPolicyTargetAdRatio === "" ? null : Number(newPolicyTargetAdRatio),
        midroll_mode: newPolicyMidrollMode,
        postroll_mode: newPolicyPostrollMode,
        day_part: newPolicyDayPartId === "" ? null : newPolicyDayPartId,
        is_default: newPolicyIsDefault,
      });
      setSnackbarMsg(t("adSlotPolicies.policyCreated"));
      loadAdSlotPolicies();
      setCreatePolicyDialogOpen(false);
      setNewPolicyName("");
      setNewPolicyMaxAdTimePerHour("");
      setNewPolicyMaxAdsPerBreak("");
      setNewPolicyMaxBreakDuration("");
      setNewPolicyMinGapBetweenBreaks("");
      setNewPolicyPreferredInterval("");
      setNewPolicyTargetAdRatio("");
      setNewPolicyMidrollMode("none");
      setNewPolicyPostrollMode("none");
      setNewPolicyDayPartId("");
      setNewPolicyIsDefault(false);
    } catch (e) {
      setSnackbarMsg(String(e));
    }
  };

  const handleOpenEditPolicy = (policy: AdSlotPolicy) => {
    setPolicyToEdit(policy);
    setEditPolicyName(policy.name);
    setEditPolicyMaxAdTimePerHour(policy.max_ad_time_per_hour != null ? String(policy.max_ad_time_per_hour) : "");
    setEditPolicyMaxAdsPerBreak(policy.max_ads_per_break != null ? String(policy.max_ads_per_break) : "");
    setEditPolicyMaxBreakDuration(policy.max_break_duration != null ? String(policy.max_break_duration) : "");
    setEditPolicyMinGapBetweenBreaks(policy.min_gap_between_breaks != null ? String(policy.min_gap_between_breaks) : "");
    setEditPolicyPreferredInterval(policy.preferred_interval != null ? String(policy.preferred_interval) : "");
    setEditPolicyTargetAdRatio(policy.target_ad_ratio != null ? String(policy.target_ad_ratio) : "");
    setEditPolicyDayPartId(policy.day_part != null ? policy.day_part : "");
    setEditPolicyIsDefault(policy.is_default ?? false);
    setEditPolicyMidrollMode(policy.midroll_mode);
    setEditPolicyPostrollMode(policy.postroll_mode);
    setEditPolicyDialogOpen(true);
  };

  const handleSaveEditPolicy = async () => {
    if (!selectedCustomer || !policyToEdit || !editPolicyName.trim()) return;
    try {
      await updateAdSlotPolicy(selectedCustomer, policyToEdit.id, {
        name: editPolicyName.trim(),
        max_ad_time_per_hour: editPolicyMaxAdTimePerHour === "" ? null : Number(editPolicyMaxAdTimePerHour),
        max_ads_per_break: editPolicyMaxAdsPerBreak === "" ? null : Number(editPolicyMaxAdsPerBreak),
        max_break_duration: editPolicyMaxBreakDuration === "" ? null : Number(editPolicyMaxBreakDuration),
        min_gap_between_breaks: editPolicyMinGapBetweenBreaks === "" ? null : Number(editPolicyMinGapBetweenBreaks),
        preferred_interval: editPolicyPreferredInterval === "" ? null : Number(editPolicyPreferredInterval),
        target_ad_ratio: editPolicyTargetAdRatio === "" ? null : Number(editPolicyTargetAdRatio),
        day_part: editPolicyDayPartId === "" ? null : editPolicyDayPartId,
        is_default: editPolicyIsDefault,
        midroll_mode: editPolicyMidrollMode,
        postroll_mode: editPolicyPostrollMode,
      });
      setSnackbarMsg(t("adSlotPolicies.policyUpdated"));
      loadAdSlotPolicies();
      setEditPolicyDialogOpen(false);
      setPolicyToEdit(null);
    } catch (e) {
      setSnackbarMsg(String(e));
    }
  };

  const handleDeletePolicyClick = (policy: AdSlotPolicy) => {
    setPolicyToDelete(policy);
    setConfirmDeletePolicyDialogOpen(true);
  };

  const handleConfirmDeletePolicy = async () => {
    if (!selectedCustomer || !policyToDelete) return;
    try {
      await deleteAdSlotPolicy(selectedCustomer, policyToDelete.id);
      setSnackbarMsg(t("adSlotPolicies.policyDeleted"));
      loadAdSlotPolicies();
      setConfirmDeletePolicyDialogOpen(false);
      setPolicyToDelete(null);
    } catch (e) {
      setSnackbarMsg(String(e));
    }
  };

  return (
    <Box>
      {/* Layout: 1/3 links für Kundenliste, 2/3 rechts für Benutzer und Player */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Linkes Drittel: Kundenliste */}
        <Box sx={{ width: { xs: '100%', md: '33.333%' } }}>
          <Paper sx={{ p: 1.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1">
                {t("customerManagement.customersHeading")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setCreateCustomerDialogOpen(true)}
              >
                {t("customerManagement.createCustomerButton")}
              </Button>
            </Box>
            <List>
              {customers.length === 0 ? (
                <ListItem>
                  <ListItemText primary={t("customerManagement.noCustomers")} sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }} />
                </ListItem>
              ) : (
                customers.map((customer) => (
                  <ListItem
                    key={customer.id}
                    component={ListItemButton}
                    onClick={() => setSelectedCustomer(customer.id)}
                    selected={selectedCustomer === customer.id}
                    secondaryAction={
                      <Box>
                        <IconButton
                          edge="end"
                          aria-label="rename"
                          onClick={(e) => handleRenameCustomer(customer, e)}
                          title={t("customerManagement.renameCustomer")}
                          sx={{ mr: 0.5 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={(e) => handleDeleteCustomerClick(customer, e)}
                          title={t("common.delete")}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={customer.name} sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }} />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Box>

        {/* Rechte zwei Drittel: Benutzer und Player untereinander */}
        <Box sx={{ width: { xs: '100%', md: '66.666%' }, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          {/* USER MANAGEMENT: Only show if customer selected */}
          {selectedCustomer && (
            <Box sx={{ width: '100%' }}>
              <UserManagement
                customerId={selectedCustomer}
                onPlayerCreated={loadPlayersForSelectedCustomer}
              />
            </Box>
          )}

          {/* Player List and Selection */}
          {selectedCustomer && (
            <Box sx={{ width: '100%' }}>
              <Paper sx={{ p: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">{t("playerManagement.playersHeading")}</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreatePlayerDialogOpen(true)}
                  sx={{ minWidth: "11rem" }}
                >
                  {t("playerManagement.createPlayerButton")}
                </Button>
              </Box>
<List dense sx={{ py: 0 }}>
              {players.length === 0 ? (
                <ListItem>
                    <ListItemText primary={t("playerManagement.noPlayersAvailable")} sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }} />
                  </ListItem>
                ) : (
                  players.map((player) => (
                    <ListItem
                      key={player.sn}
                      secondaryAction={
                        <Box>
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={(e) => handleEditPlayerClick(player, e)}
                            title={t("playerManagement.editPlayer")}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="assign-content"
                            onClick={() => handleOpenContentAssignmentDialog(player.sn)}
                            title={t("contentManagement.assignContentTitle")}
                            sx={{ mr: 1 }}
                          >
                            <ContentPasteIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="open-feed-config"
                            onClick={() => setShowFeedConfig(player.sn)}
                            title={t("adminPanel.openFeedConfig")}
                            sx={{ mr: 1 }}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="clear-content"
                            onClick={() => handleClearContentClick(player.sn)}
                            title={t("playerManagement.clearContent")}
                            color="warning"
                            sx={{ mr: 1 }}
                          >
                            <DeleteSweepIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeletePlayerClick(player.sn)}
                            title={t("common.delete")}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText 
                        primary={`${player.sn} (${player.location}${player.time_zone ? `, ${player.time_zone}` : ''})`} 
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }} 
                      />
                    </ListItem>
                  ))
                )}
              </List>
              </Paper>
            </Box>
          )}

          {/* Tageszeiten (Day parts) */}
          {selectedCustomer && (
            <Box sx={{ width: "100%" }}>
              <Paper sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">{t("dayParts.sectionTitle")}</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDayPartDialogOpen(true)}
                    sx={{ minWidth: "11rem" }}
                  >
                    {t("dayParts.createButton")}
                  </Button>
                </Box>
                <List dense sx={{ py: 0 }}>
                  {dayParts.length === 0 ? (
                    <ListItem>
                      <ListItemText primary={t("dayParts.noDayParts")} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
                    </ListItem>
                  ) : (
                    dayParts.map((dp) => (
                      <ListItem
                        key={dp.id}
                        secondaryAction={
                          <Box>
                            <IconButton edge="end" onClick={() => handleOpenEditDayPart(dp)} title={t("adSlotPolicies.editButton")} sx={{ mr: 0.5 }}>
                              <EditIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={() => handleDeleteDayPartClick(dp)} title={t("common.delete")} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText primary={`${dp.name} (${String(dp.from_time).slice(0, 5)})`} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
                      </ListItem>
                    ))
                  )}
                </List>
              </Paper>
            </Box>
          )}

          {/* Werbe-Slot Richtlinie (Ad-Slot-Policies) */}
          {selectedCustomer && (
            <Box sx={{ width: "100%" }}>
              <Paper sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">{t("adSlotPolicies.sectionTitle")}</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreatePolicyDialogOpen(true)}
                    sx={{ minWidth: "11rem" }}
                  >
                    {t("adSlotPolicies.createButton")}
                  </Button>
                </Box>
                <List dense sx={{ py: 0 }}>
                  {adSlotPolicies.length === 0 ? (
                    <ListItem>
                      <ListItemText primary={t("adSlotPolicies.noPolicies")} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
                    </ListItem>
                  ) : (
                    adSlotPolicies.map((policy) => {
                      const dayPartName = policy.day_part != null ? dayParts.find((dp) => dp.id === policy.day_part)?.name : null;
                      const secondaryParts: string[] = [];
                      if (policy.is_default) secondaryParts.push(t("adSlotPolicies.defaultEntryLabel"));
                      if (dayPartName != null) secondaryParts.push(`${t("adSlotPolicies.validityPrefix")} ${dayPartName}`);
                      const secondary = secondaryParts.length > 0 ? secondaryParts.join(" · ") : undefined;
                      return (
                        <ListItem
                          key={policy.id}
                          secondaryAction={
                            <Box>
                              <IconButton edge="end" onClick={() => handleOpenEditPolicy(policy)} title={t("adSlotPolicies.editButton")} sx={{ mr: 0.5 }}>
                                <EditIcon />
                              </IconButton>
                              <IconButton edge="end" onClick={() => handleDeletePolicyClick(policy)} title={t("common.delete")} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={policy.name}
                            secondary={secondary}
                            sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" }, "& .MuiListItemText-secondary": { fontSize: "0.8rem" } }}
                          />
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Paper>
            </Box>
          )}
        </Box>
      </Box>

      {/* MODAL FEED CONFIGURATION WINDOW */}
      <Dialog
        open={!!showFeedConfig}
        onClose={() => setShowFeedConfig(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: "70vh" } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
          {t("adminPanel.feedConfigForPlayer", { playerSn: showFeedConfig })}
          {selectedCustomer && (
            t("adminPanel.customerInfo", { customerName: customers.find(c => c.id === selectedCustomer)?.name })
          )}
          <IconButton
            aria-label="close"
            onClick={() => setShowFeedConfig(null)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {showFeedConfig && <Feeds adminPlayer={showFeedConfig} />}
        </DialogContent>
      </Dialog>

      {/* CONFIRMATION DIALOG for deleting players */}
      <Dialog
        open={confirmDeletePlayerDialogOpen}
        onClose={handleCancelDeletePlayer}
        aria-labelledby="confirm-delete-player-title"
        aria-describedby="confirm-delete-player-description"
      >
        <DialogTitle id="confirm-delete-player-title">{t("playerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-player-description">
            {t("playerManagement.confirmDeleteMessage", { playerSN: playerToDeleteSN })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeletePlayer} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeletePlayer} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMATION DIALOG for clearing player content */}
      <Dialog
        open={confirmClearContentDialogOpen}
        onClose={handleCancelClearContent}
        aria-labelledby="confirm-clear-content-title"
        aria-describedby="confirm-clear-content-description"
      >
        <DialogTitle id="confirm-clear-content-title">{t("playerManagement.confirmClearContentTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-clear-content-description">
            {t("playerManagement.confirmClearContentMessage", { playerSN: playerToClearContentSN })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClearContent} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmClearContent} color="warning" autoFocus>{t("playerManagement.clearContent")}</Button>
        </DialogActions>
      </Dialog>

      {/* NEW: Content Assignment Dialog */}
      <Dialog open={contentAssignmentDialogOpen} onClose={() => setContentAssignmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("contentManagement.assignContentDialogTitle", { playerSN: selectedPlayerForContentAssignment })}</DialogTitle>
        <DialogContent>
          {loadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} /> {/* Ladeanzeige */}
              <Typography>{t("contentManagement.loadingContent")}</Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}> {/* Direkte Selektionsliste mit Scrollbalken */}
              {allAvailableContent.length === 0 ? (
                <ListItem>
                  <ListItemText primary={t("contentManagement.noContentAvailable")} />
                </ListItem>
              ) : (
                allAvailableContent.map((content) => (
                  <ListItemButton
                    key={content.file_name}
                    onClick={() => handleToggleContentAssignment(content.content_name)}
                    dense // Macht die Listeneinträge kompakter
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={assignedContentNames.indexOf(content.content_name) > -1}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={`${content.content_name} (${content.file_name})`} />
                  </ListItemButton>
                ))
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContentAssignmentDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveContentAssignment} disabled={loadingContent}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* RENAME CUSTOMER DIALOG */}
      <Dialog open={renameCustomerDialogOpen} onClose={handleCancelRenameCustomer}>
        <DialogTitle>{t("customerManagement.renameCustomerDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("customerManagement.customerNameLabel")}
            fullWidth
            variant="standard"
            value={newCustomerName}
            onChange={(e) => setNewCustomerName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRenameCustomer}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveRenameCustomer} disabled={!newCustomerName.trim()}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CREATE CUSTOMER DIALOG */}
      <Dialog open={createCustomerDialogOpen} onClose={() => setCreateCustomerDialogOpen(false)}>
        <DialogTitle>{t("customerManagement.createCustomerDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("customerManagement.customerNameLabel")}
            fullWidth
            variant="standard"
            value={newCustomerNameInput}
            onChange={(e) => setNewCustomerNameInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCustomerDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreateCustomer} disabled={!newCustomerNameInput.trim()}>
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CUSTOMER CONFIRMATION DIALOG */}
      <Dialog
        open={confirmDeleteCustomerDialogOpen}
        onClose={handleCancelDeleteCustomer}
        aria-labelledby="confirm-delete-customer-title"
        aria-describedby="confirm-delete-customer-description"
      >
        <DialogTitle id="confirm-delete-customer-title">{t("customerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-customer-description">
            {t("customerManagement.confirmDeleteMessage", { customerName: customerToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteCustomer} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteCustomer} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* CREATE AD-SLOT-POLICY DIALOG */}
      <Dialog open={createPolicyDialogOpen} onClose={() => setCreatePolicyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("adSlotPolicies.createDialogTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField label={t("adSlotPolicies.designationLabel")} value={newPolicyName} onChange={(e) => setNewPolicyName(e.target.value)} fullWidth />
          <TextField type="number" label={t("adSlotPolicies.maxAdTimePerHourLabel")} value={newPolicyMaxAdTimePerHour} onChange={(e) => setNewPolicyMaxAdTimePerHour(e.target.value)} fullWidth />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>{t("adSlotPolicies.slotGroupLabel")}</Typography>
          <TextField type="number" label={t("adSlotPolicies.maxAdsPerBreakLabel")} value={newPolicyMaxAdsPerBreak} onChange={(e) => setNewPolicyMaxAdsPerBreak(e.target.value)} fullWidth />
          <TextField type="number" label={t("adSlotPolicies.maxBreakDurationLabel")} value={newPolicyMaxBreakDuration} onChange={(e) => setNewPolicyMaxBreakDuration(e.target.value)} fullWidth />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>{t("adSlotPolicies.gapGroupLabel")}</Typography>
          <TextField type="number" label={t("adSlotPolicies.minGapBetweenBreaksLabel")} value={newPolicyMinGapBetweenBreaks} onChange={(e) => setNewPolicyMinGapBetweenBreaks(e.target.value)} fullWidth />
          <TextField type="number" label={t("adSlotPolicies.preferredIntervalLabel")} value={newPolicyPreferredInterval} onChange={(e) => setNewPolicyPreferredInterval(e.target.value)} fullWidth />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>{t("adSlotPolicies.timingGroupLabel")}</Typography>
          <FormControl fullWidth>
            <InputLabel>{t("adSlotPolicies.midrollModeLabel")}</InputLabel>
            <Select value={newPolicyMidrollMode} onChange={(e) => setNewPolicyMidrollMode(e.target.value as "none" | "breakpoint" | "anytime")} label={t("adSlotPolicies.midrollModeLabel")}>
              <MenuItem value="none">{t("adSlotPolicies.midrollNone")}</MenuItem>
              <MenuItem value="breakpoint">{t("adSlotPolicies.midrollBreakpoint")}</MenuItem>
              <MenuItem value="anytime">{t("adSlotPolicies.midrollAnytime")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t("adSlotPolicies.postrollModeLabel")}</InputLabel>
            <Select value={newPolicyPostrollMode} onChange={(e) => setNewPolicyPostrollMode(e.target.value as "none" | "active")} label={t("adSlotPolicies.postrollModeLabel")}>
              <MenuItem value="none">{t("adSlotPolicies.postrollNone")}</MenuItem>
              <MenuItem value="active">{t("adSlotPolicies.postrollActive")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t("adSlotPolicies.dayPartLabel")}</InputLabel>
            <Select value={newPolicyDayPartId === "" ? "" : newPolicyDayPartId} onChange={(e) => setNewPolicyDayPartId(e.target.value === "" ? "" : Number(e.target.value))} label={t("adSlotPolicies.dayPartLabel")}>
              <MenuItem value="">—</MenuItem>
              {dayParts.map((dp) => (
                <MenuItem key={dp.id} value={dp.id}>{dp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox checked={newPolicyIsDefault} onChange={(e) => setNewPolicyIsDefault(e.target.checked)} />
            <Typography variant="body2">{t("adSlotPolicies.defaultLabel")}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePolicyDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreatePolicy} disabled={!newPolicyName.trim()}>{t("common.create")}</Button>
        </DialogActions>
      </Dialog>

      {/* EDIT AD-SLOT-POLICY DIALOG */}
      <Dialog open={editPolicyDialogOpen} onClose={() => { setEditPolicyDialogOpen(false); setPolicyToEdit(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" } }}>
        <DialogTitle sx={{ flexShrink: 0 }}>{t("adSlotPolicies.editDialogTitle")}</DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            pt: 1,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "grey.200", borderRadius: 1 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: 4,
              "&:hover": { backgroundColor: "grey.500" },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.08)",
          }}
        >
          <TextField label={t("adSlotPolicies.designationLabel")} value={editPolicyName} onChange={(e) => setEditPolicyName(e.target.value)} fullWidth />
          <TextField type="number" label={t("adSlotPolicies.maxAdTimePerHourLabel")} value={editPolicyMaxAdTimePerHour} onChange={(e) => setEditPolicyMaxAdTimePerHour(e.target.value)} fullWidth />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>{t("adSlotPolicies.validityGroupLabel")}</Typography>
          <FormControl fullWidth>
            <InputLabel>{t("adSlotPolicies.dayPartLabel")}</InputLabel>
            <Select value={editPolicyDayPartId === "" ? "" : editPolicyDayPartId} onChange={(e) => setEditPolicyDayPartId(e.target.value === "" ? "" : Number(e.target.value))} label={t("adSlotPolicies.dayPartLabel")}>
              <MenuItem value="">—</MenuItem>
              {dayParts.map((dp) => (
                <MenuItem key={dp.id} value={dp.id}>{dp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox checked={editPolicyIsDefault} onChange={(e) => setEditPolicyIsDefault(e.target.checked)} />
            <Typography variant="body2">{t("adSlotPolicies.defaultLabel")}</Typography>
          </Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>{t("adSlotPolicies.slotGroupLabel")}</Typography>
          <TextField type="number" label={t("adSlotPolicies.maxAdsPerBreakLabel")} value={editPolicyMaxAdsPerBreak} onChange={(e) => setEditPolicyMaxAdsPerBreak(e.target.value)} fullWidth />
          <TextField type="number" label={t("adSlotPolicies.maxBreakDurationLabel")} value={editPolicyMaxBreakDuration} onChange={(e) => setEditPolicyMaxBreakDuration(e.target.value)} fullWidth />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>{t("adSlotPolicies.gapGroupLabel")}</Typography>
          <TextField type="number" label={t("adSlotPolicies.minGapBetweenBreaksLabel")} value={editPolicyMinGapBetweenBreaks} onChange={(e) => setEditPolicyMinGapBetweenBreaks(e.target.value)} fullWidth />
          <TextField type="number" label={t("adSlotPolicies.preferredIntervalLabel")} value={editPolicyPreferredInterval} onChange={(e) => setEditPolicyPreferredInterval(e.target.value)} fullWidth />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>{t("adSlotPolicies.timingGroupLabel")}</Typography>
          <FormControl fullWidth>
            <InputLabel>{t("adSlotPolicies.midrollModeLabel")}</InputLabel>
            <Select value={editPolicyMidrollMode} onChange={(e) => setEditPolicyMidrollMode(e.target.value as "none" | "breakpoint" | "anytime")} label={t("adSlotPolicies.midrollModeLabel")}>
              <MenuItem value="none">{t("adSlotPolicies.midrollNone")}</MenuItem>
              <MenuItem value="breakpoint">{t("adSlotPolicies.midrollBreakpoint")}</MenuItem>
              <MenuItem value="anytime">{t("adSlotPolicies.midrollAnytime")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t("adSlotPolicies.postrollModeLabel")}</InputLabel>
            <Select value={editPolicyPostrollMode} onChange={(e) => setEditPolicyPostrollMode(e.target.value as "none" | "active")} label={t("adSlotPolicies.postrollModeLabel")}>
              <MenuItem value="none">{t("adSlotPolicies.postrollNone")}</MenuItem>
              <MenuItem value="active">{t("adSlotPolicies.postrollActive")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={() => { setEditPolicyDialogOpen(false); setPolicyToEdit(null); }}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveEditPolicy} disabled={!editPolicyName.trim()}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE AD-SLOT-POLICY CONFIRMATION */}
      <Dialog open={confirmDeletePolicyDialogOpen} onClose={() => { setConfirmDeletePolicyDialogOpen(false); setPolicyToDelete(null); }}>
        <DialogTitle>{t("customerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("adSlotPolicies.confirmDelete", { name: policyToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDeletePolicyDialogOpen(false); setPolicyToDelete(null); }}>{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeletePolicy} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* CREATE DAY PART DIALOG */}
      <Dialog open={createDayPartDialogOpen} onClose={() => setCreateDayPartDialogOpen(false)}>
        <DialogTitle>{t("dayParts.createDialogTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField label={t("dayParts.nameLabel")} value={newDayPartName} onChange={(e) => setNewDayPartName(e.target.value)} fullWidth />
          <TextField type="time" label={t("dayParts.fromTimeLabel")} value={newDayPartFromTime} onChange={(e) => setNewDayPartFromTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDayPartDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreateDayPart} disabled={!newDayPartName.trim() || !newDayPartFromTime}>{t("common.create")}</Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DAY PART DIALOG */}
      <Dialog open={editDayPartDialogOpen} onClose={() => { setEditDayPartDialogOpen(false); setDayPartToEdit(null); }}>
        <DialogTitle>{t("dayParts.editDialogTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField label={t("dayParts.nameLabel")} value={editDayPartName} onChange={(e) => setEditDayPartName(e.target.value)} fullWidth />
          <TextField type="time" label={t("dayParts.fromTimeLabel")} value={editDayPartFromTime} onChange={(e) => setEditDayPartFromTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDayPartDialogOpen(false); setDayPartToEdit(null); }}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveEditDayPart} disabled={!editDayPartName.trim() || !editDayPartFromTime}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DAY PART CONFIRMATION */}
      <Dialog open={confirmDeleteDayPartDialogOpen} onClose={() => { setConfirmDeleteDayPartDialogOpen(false); setDayPartToDelete(null); }}>
        <DialogTitle>{t("customerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("dayParts.confirmDelete", { name: dayPartToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDeleteDayPartDialogOpen(false); setDayPartToDelete(null); }}>{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteDayPart} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* CREATE PLAYER DIALOG */}
      <Dialog open={createPlayerDialogOpen} onClose={() => {
        setCreatePlayerDialogOpen(false);
        setNewPlayerSN("");
        setNewPlayerLocation("");
        setNewPlayerTimeZone("");
      }}>
        <DialogTitle>{t("playerManagement.createPlayerDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("playerManagement.playerSNLabel")}
            fullWidth
            variant="standard"
            value={newPlayerSN}
            onChange={(e) => setNewPlayerSN(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t("playerManagement.playerLocationLabel")}
            fullWidth
            variant="standard"
            value={newPlayerLocation}
            onChange={(e) => setNewPlayerLocation(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" variant="standard" required>
            <InputLabel>{t("playerManagement.playerTimeZoneLabel")}</InputLabel>
            <Select
              value={newPlayerTimeZone}
              onChange={(e) => setNewPlayerTimeZone(e.target.value)}
              label={t("playerManagement.playerTimeZoneLabel")}
            >
              <MenuItem value="Europe/Berlin">{getUTCOffset("Europe/Berlin")} Europe/Berlin</MenuItem>
              <MenuItem value="Europe/London">{getUTCOffset("Europe/London")} Europe/London</MenuItem>
              <MenuItem value="Europe/Paris">{getUTCOffset("Europe/Paris")} Europe/Paris</MenuItem>
              <MenuItem value="Europe/Madrid">{getUTCOffset("Europe/Madrid")} Europe/Madrid</MenuItem>
              <MenuItem value="Europe/Rome">{getUTCOffset("Europe/Rome")} Europe/Rome</MenuItem>
              <MenuItem value="Europe/Vienna">{getUTCOffset("Europe/Vienna")} Europe/Vienna</MenuItem>
              <MenuItem value="Europe/Zurich">{getUTCOffset("Europe/Zurich")} Europe/Zurich</MenuItem>
              <MenuItem value="Europe/Amsterdam">{getUTCOffset("Europe/Amsterdam")} Europe/Amsterdam</MenuItem>
              <MenuItem value="Europe/Brussels">{getUTCOffset("Europe/Brussels")} Europe/Brussels</MenuItem>
              <MenuItem value="Europe/Stockholm">{getUTCOffset("Europe/Stockholm")} Europe/Stockholm</MenuItem>
              <MenuItem value="Europe/Copenhagen">{getUTCOffset("Europe/Copenhagen")} Europe/Copenhagen</MenuItem>
              <MenuItem value="Europe/Oslo">{getUTCOffset("Europe/Oslo")} Europe/Oslo</MenuItem>
              <MenuItem value="Europe/Helsinki">{getUTCOffset("Europe/Helsinki")} Europe/Helsinki</MenuItem>
              <MenuItem value="Europe/Warsaw">{getUTCOffset("Europe/Warsaw")} Europe/Warsaw</MenuItem>
              <MenuItem value="Europe/Prague">{getUTCOffset("Europe/Prague")} Europe/Prague</MenuItem>
              <MenuItem value="Europe/Budapest">{getUTCOffset("Europe/Budapest")} Europe/Budapest</MenuItem>
              <MenuItem value="Europe/Athens">{getUTCOffset("Europe/Athens")} Europe/Athens</MenuItem>
              <MenuItem value="America/New_York">{getUTCOffset("America/New_York")} America/New_York</MenuItem>
              <MenuItem value="America/Chicago">{getUTCOffset("America/Chicago")} America/Chicago</MenuItem>
              <MenuItem value="America/Denver">{getUTCOffset("America/Denver")} America/Denver</MenuItem>
              <MenuItem value="America/Los_Angeles">{getUTCOffset("America/Los_Angeles")} America/Los_Angeles</MenuItem>
              <MenuItem value="America/Toronto">{getUTCOffset("America/Toronto")} America/Toronto</MenuItem>
              <MenuItem value="America/Mexico_City">{getUTCOffset("America/Mexico_City")} America/Mexico_City</MenuItem>
              <MenuItem value="America/Sao_Paulo">{getUTCOffset("America/Sao_Paulo")} America/Sao_Paulo</MenuItem>
              <MenuItem value="Asia/Tokyo">{getUTCOffset("Asia/Tokyo")} Asia/Tokyo</MenuItem>
              <MenuItem value="Asia/Shanghai">{getUTCOffset("Asia/Shanghai")} Asia/Shanghai</MenuItem>
              <MenuItem value="Asia/Hong_Kong">{getUTCOffset("Asia/Hong_Kong")} Asia/Hong_Kong</MenuItem>
              <MenuItem value="Asia/Singapore">{getUTCOffset("Asia/Singapore")} Asia/Singapore</MenuItem>
              <MenuItem value="Asia/Dubai">{getUTCOffset("Asia/Dubai")} Asia/Dubai</MenuItem>
              <MenuItem value="Asia/Kolkata">{getUTCOffset("Asia/Kolkata")} Asia/Kolkata</MenuItem>
              <MenuItem value="Australia/Sydney">{getUTCOffset("Australia/Sydney")} Australia/Sydney</MenuItem>
              <MenuItem value="Australia/Melbourne">{getUTCOffset("Australia/Melbourne")} Australia/Melbourne</MenuItem>
              <MenuItem value="Pacific/Auckland">{getUTCOffset("Pacific/Auckland")} Pacific/Auckland</MenuItem>
              <MenuItem value="UTC">{getUTCOffset("UTC")} UTC</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePlayerDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreatePlayer} disabled={!newPlayerSN.trim() || !newPlayerLocation.trim() || !newPlayerTimeZone.trim()}>
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT PLAYER DIALOG */}
      <Dialog open={editPlayerDialogOpen} onClose={handleCancelEditPlayer}>
        <DialogTitle>{t("playerManagement.editPlayerDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("playerManagement.playerLocationLabel")}
            fullWidth
            variant="standard"
            value={editPlayerLocation}
            onChange={(e) => setEditPlayerLocation(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" variant="standard" required>
            <InputLabel>{t("playerManagement.playerTimeZoneLabel")}</InputLabel>
            <Select
              value={editPlayerTimeZone}
              onChange={(e) => setEditPlayerTimeZone(e.target.value)}
              label={t("playerManagement.playerTimeZoneLabel")}
            >
              <MenuItem value="Europe/Berlin">{getUTCOffset("Europe/Berlin")} Europe/Berlin</MenuItem>
              <MenuItem value="Europe/London">{getUTCOffset("Europe/London")} Europe/London</MenuItem>
              <MenuItem value="Europe/Paris">{getUTCOffset("Europe/Paris")} Europe/Paris</MenuItem>
              <MenuItem value="Europe/Madrid">{getUTCOffset("Europe/Madrid")} Europe/Madrid</MenuItem>
              <MenuItem value="Europe/Rome">{getUTCOffset("Europe/Rome")} Europe/Rome</MenuItem>
              <MenuItem value="Europe/Vienna">{getUTCOffset("Europe/Vienna")} Europe/Vienna</MenuItem>
              <MenuItem value="Europe/Zurich">{getUTCOffset("Europe/Zurich")} Europe/Zurich</MenuItem>
              <MenuItem value="Europe/Amsterdam">{getUTCOffset("Europe/Amsterdam")} Europe/Amsterdam</MenuItem>
              <MenuItem value="Europe/Brussels">{getUTCOffset("Europe/Brussels")} Europe/Brussels</MenuItem>
              <MenuItem value="Europe/Stockholm">{getUTCOffset("Europe/Stockholm")} Europe/Stockholm</MenuItem>
              <MenuItem value="Europe/Copenhagen">{getUTCOffset("Europe/Copenhagen")} Europe/Copenhagen</MenuItem>
              <MenuItem value="Europe/Oslo">{getUTCOffset("Europe/Oslo")} Europe/Oslo</MenuItem>
              <MenuItem value="Europe/Helsinki">{getUTCOffset("Europe/Helsinki")} Europe/Helsinki</MenuItem>
              <MenuItem value="Europe/Warsaw">{getUTCOffset("Europe/Warsaw")} Europe/Warsaw</MenuItem>
              <MenuItem value="Europe/Prague">{getUTCOffset("Europe/Prague")} Europe/Prague</MenuItem>
              <MenuItem value="Europe/Budapest">{getUTCOffset("Europe/Budapest")} Europe/Budapest</MenuItem>
              <MenuItem value="Europe/Athens">{getUTCOffset("Europe/Athens")} Europe/Athens</MenuItem>
              <MenuItem value="America/New_York">{getUTCOffset("America/New_York")} America/New_York</MenuItem>
              <MenuItem value="America/Chicago">{getUTCOffset("America/Chicago")} America/Chicago</MenuItem>
              <MenuItem value="America/Denver">{getUTCOffset("America/Denver")} America/Denver</MenuItem>
              <MenuItem value="America/Los_Angeles">{getUTCOffset("America/Los_Angeles")} America/Los_Angeles</MenuItem>
              <MenuItem value="America/Toronto">{getUTCOffset("America/Toronto")} America/Toronto</MenuItem>
              <MenuItem value="America/Mexico_City">{getUTCOffset("America/Mexico_City")} America/Mexico_City</MenuItem>
              <MenuItem value="America/Sao_Paulo">{getUTCOffset("America/Sao_Paulo")} America/Sao_Paulo</MenuItem>
              <MenuItem value="Asia/Tokyo">{getUTCOffset("Asia/Tokyo")} Asia/Tokyo</MenuItem>
              <MenuItem value="Asia/Shanghai">{getUTCOffset("Asia/Shanghai")} Asia/Shanghai</MenuItem>
              <MenuItem value="Asia/Hong_Kong">{getUTCOffset("Asia/Hong_Kong")} Asia/Hong_Kong</MenuItem>
              <MenuItem value="Asia/Singapore">{getUTCOffset("Asia/Singapore")} Asia/Singapore</MenuItem>
              <MenuItem value="Asia/Dubai">{getUTCOffset("Asia/Dubai")} Asia/Dubai</MenuItem>
              <MenuItem value="Asia/Kolkata">{getUTCOffset("Asia/Kolkata")} Asia/Kolkata</MenuItem>
              <MenuItem value="Australia/Sydney">{getUTCOffset("Australia/Sydney")} Australia/Sydney</MenuItem>
              <MenuItem value="Australia/Melbourne">{getUTCOffset("Australia/Melbourne")} Australia/Melbourne</MenuItem>
              <MenuItem value="Pacific/Auckland">{getUTCOffset("Pacific/Auckland")} Pacific/Auckland</MenuItem>
              <MenuItem value="UTC">{getUTCOffset("UTC")} UTC</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEditPlayer}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveEditPlayer} disabled={!editPlayerLocation.trim() || !editPlayerTimeZone.trim()}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg(null)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};

export default AdminPanel;
